<template>
	<div id="settings" class="page" v-if="!this.$parent.loading && init">
		<div class="columns">
			<div class="column" v-if="userCan('view_company_details', 'companies')">
				<div class="panel">
					<h5 class="panel-heading">Your Company</h5>
					<form class="panel-block">
						<b-loading :is-full-page="false" :active.sync="loading" :can-cancel="false"></b-loading>
						<section>
							<b-field label="Company Name"
							         :type="fieldSet.name.type"
							         :message="fieldSet.name.message">
								<b-input v-model="companyName"
								         @input="clearError('name')"></b-input>
							</b-field>
							<b-field label="Language"
							         :type="fieldSet.language.type"
							         :message="fieldSet.language.message">
								<b-select placeholder="Select an available language"
								          :value="language"
								          @input="clearError('language')">
									<option
										v-for="language in languages"
										:value="language.value"
										:key="language.value">
										{{ language.text }}
									</option>
								</b-select>
							</b-field>
							<b-field label="Time Zone"
							         :type="fieldSet.timezone.type"
							         :message="fieldSet.timezone.message">
								<b-select placeholder="Select an available timezone"
								          v-model="timezone"
								          @input="clearError('timezone')">
									<option
										v-for="timezone in timezones"
										:value="timezone.value"
										:key="timezone.value">
										{{ timezone.label }}
									</option>
								</b-select>
							</b-field>

							<hr class="divider" />

							<b-field label="Company Logo"
							         :addons="false"
							         :type="fieldSet.timezone.type"
							         :message="fieldSet.timezone.message">
								<p>Upload your company logo to display your brand.</p>
								<p v-if="currentLogo">
									<img :src="currentLogo" class="company-logo" alt="logo" />
								</p>
								<b-field class="file">
									<b-upload v-model="logo" v-on:input="logoChanged">
										<a class="button is-white has-icon">
											<b-icon icon="upload"></b-icon>
											<span>Click to upload</span>
										</a>
									</b-upload>
									<span class="file-name" v-if="logoFileName">{{ logoFileName }}</span>
									<a href="#" class="clear-field" title="Remove File / Cancel" v-on:click.prevent="resetLogo">
										<b-icon v-if="logoFileName"
										        icon="trash" type="is-danger" class="m-l-15" />
									</a>
								</b-field>
							</b-field>

							<hr class="divider" />
							<b-button v-on:click="updateCompany" type="is-primary">Save Changes</b-button>
						</section>
					</form>
				</div>
			</div>
			<div class="column">
				<h4 class="title is-4">
					Subscription is
					<span v-if="subscription.id !== '0'">Active</span>
					<b-icon icon="check-circle"
					        v-if="subscription.id !== '0'"
					        :type="subscriptionType" />
					<span v-if="subscription.id === '0'">Inactive</span>
					<b-icon icon="times-circle"
					        v-if="subscription.id === '0'"
					        :type="subscriptionType" />
				</h4>
				<b-notification
					v-if="subscription.id !== '0'"
					:type="subscriptionType"
					:closable="false">
					Your subscription is active since <strong>{{ subscription.created_at }}</strong> and you are on a
					<strong>{{ subscription.term }}</strong> term. Please contact us to make changes or if you require any assistance.
				</b-notification>
				<b-notification
					v-if="subscription.id === '0'"
					:type="subscriptionType"
					:closable="false">
					<strong>Your company does not have a valid Subscription.</strong> Please contact us to make changes or if you require any assistance.
				</b-notification>
				<div id="company-statuses" class="panel" v-if="userCan('view_company_statuses', 'status')">
					<div class="panel-heading">System Statuses</div>
					<div class="panel-block">
						<b-loading :active="loadingStatuses" :is-full-page="false" />
						<form v-if="!loadingStatuses">
							<b-tabs v-model="statusTab" position="is-centered" class="block">
								<template v-for="(key, label) in statusGroups">
									<b-tab-item :key="key" v-if="key !== 'risk_control' || userCan('view_risk_control', 'accounts')">
										<template slot="header">
											<b-icon :icon="statusesIcons[key]"></b-icon>
											<span>{{ label }}</span>
										</template>
										<div class="status"
										     :class="status.model_type"
										     v-for="(status, index) in statuses"
										     :key="`status-${index}`" v-if="status.model_type === key">
											<swatches v-model="status.color"
											          v-on:input="updateStatus(status.id, index)"
											          swatch-size="28"
											          :trigger-style="{ width: '35px', height: '35px' }" />
											<b-input v-model="status.name"
											         v-on:input="updateStatus(status.id, index)"
											         :disabled="!userCan('update_statuses', 'status')"
											         placeholder="Status Name" />
											<div v-if="status.model_type === 'simple_account'">
												<b-select v-model="status.state"
												          v-on:input="updateStatus(status.id, index)"
												          placeholder="Account State">
													<option value="uploading" key="uploading">Uploading</option>
													<option value="processing" key="processing">Processing</option>
													<option value="active" key="active">Activated</option>
												</b-select>
											</div>
											<b-button type="is-light" title="Remove Status" v-on:click="removeStatus(status.id, index)">
												<b-icon icon="minus" type="is-danger" />
											</b-button>
										</div>
										<a class="button is-success new-status pull-right has-icon"
										   href="#"
										   v-if="userCan('create_statuses', 'status')"
										   v-on:click.prevent="addStatus(key)">
											<b-icon icon="plus" />
											<span>Add New Status</span>
										</a>
									</b-tab-item>
								</template>
							</b-tabs>
						</form>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapMutations, mapGetters } from 'vuex'
import { COMPANY_GET, COMPANY_UPDATE } from '../store/actions/companies'
import { INIT_GET } from '@/store/actions/init'
import Swatches from 'vue-swatches'
import 'vue-swatches/dist/vue-swatches.min.css'
import apiCall from '../store/modules/call'

export default {
  name: 'Settings',
  data () {
    return {
      loading: true,
      loadingStatuses: true,
      company: {},
      statuses: {},
      statusesGroups: {
        'Accounts': 'account',
        'Simple Accounts': 'simple_account',
        'Risk Control': 'risk_control'
      },
      statusesIcons: {
        'account': 'briefcase',
        'simple_account': 'briefcase',
        'risk_control': 'file-medical-alt'
      },
      companyName: '',
      language: '',
      timezone: '',
      logo: {},
      logo_url: '',
      selectedTimeZone: null,
      languages: [
        {
          value: 'en',
          text: 'English'
        }
      ],
      fieldSet: {
        'name': {},
        'language': {},
        'timezone': {},
        'logo_url': {}
      },
      statusTab: 0,
      swatches: ['']
    }
  },
  components: {
    Swatches
  },
  computed: {
    ...mapState({
      init: state => state.init.all
    }),
    companyId: function () {
      if (this.company.id) return this.company.id
      if (this.init) return this.init.auth_user.company.id
      return ''
    },
    subscription: function () {
      if (!this.init) return ''
      return this.init.auth_user.subscription
    },
    subscriptionType: function () {
      if (this.subscription.id !== '0') return 'is-success'
      return 'is-danger'
    },
    currentLogo: function () {
      if (this.company.logo_url) return this.company.logo_url
      if (this.logo_url) return this.logo_url

      return null
    },
    timezones: function () {
      return [
        { label: 'Hawaii-Aleutian Standard Time', value: 'America/Adak' },
        { label: 'Alaskan Standard Time', value: 'America/Anchorage' },
        { label: 'Pacific Standard Time', value: 'America/Los_Angeles' },
        { label: 'Mountain Standard Time', value: 'America/Denver' },
        { label: 'Central Standard Time', value: 'America/Chicago' },
        { label: 'Eastern Standard Time', value: 'America/New_York' },
        { label: 'Atlantic Standard Time', value: 'Atlantic/Bermuda' }
      ]
    },
    logoFileName () {
      if (this.logo.name !== undefined) return this.logo.name
      if (!this.currentLogo) return

      let parts = this.currentLogo.split('/')
      return parts.pop()
    },
    statusGroups () {
      if (this.userCan('manage_simple_accounts', 'accounts')) {
        return this.statusesGroups
      } else {
        let filteredGroups = this.statusesGroups
        delete filteredGroups['Simple Accounts']
        return filteredGroups
      }
    },
    ...mapGetters({
      userCan: 'userCan'
    })
  },
  watch: {
    company: function () {
      this.companyName = this.company.name
      this.language = this.company.language
      this.timezone = this.company.timezone
    },
    init: function () {
      this.getCompany()
      this.setPageTitle('Settings')
    }
  },
  methods: {
    ...mapMutations([
      'setPageTitle'
    ]),
    handleError (err) {
      if (err.hasOwnProperty('response')) {
        if (err.response.data.message === 'error') {
          this.$awn.warning(err.response.data.message)
        } else if (err.response.data.message) {
          this.$awn.alert(err.response.data.message)
        }
        if (err.response.data.errors) {
          for (let field in err.response.data.errors) {
            if (!err.response.data.errors.hasOwnProperty(field)) continue
            this.$set(this.fieldSet, field, {
              'type': 'is-danger',
              'message': err.response.data.errors[field][0]
            })
          }
        }
      } else {
        this.$awn.alert(err.toString())
      }
    },
    resetLogo () {
      this.logo = {}
      this.logo_url = ''
      this.company.logo_url = ''
    },
    logoChanged () {
      if (!this.logo.type) return

      let allowedFileTypes = ['image/jpeg', 'image/gif', 'image/png', 'image/svg+xml', 'image/tiff', 'image/webp', 'image/bmp']
      if (!allowedFileTypes.includes(this.logo.type)) {
        this.logo = {}
        this.$awn.warning('Invalid image file type, logo must be an image.')
      } else {
        this.logo_url = ''
        this.company.logo_url = ''
      }
    },
    getCompany () {
      this.loading = true
      this.$store.dispatch(COMPANY_GET, this.companyId).then(response => {
        this.company = response.data
        this.logo_url = this.company.logo_url
        this.loading = false
      }).catch(err => {
        this.loading = false
        this.handleError(err)
      })
    },
    getCompanyStatuses () {
      apiCall({
        url: 'statuses',
        method: 'GET'
      }).then(response => {
        this.statuses = response.data
        this.loadingStatuses = false
      })
    },
    addStatus (statusType) {
      this.statuses.push({
        'model_type': statusType,
        'name': '',
        'color': '#000000'
      })
    },
    createStatus (statusIndex) {
      apiCall({ // Get signed URL to upload
        url: 'statuses/',
        data: this.statuses[statusIndex],
        method: 'POST'
      }).then(response => {
        this.statuses.splice(statusIndex, 1)
        this.statuses.push(response.data)
        this.$awn.success(response.message)
        this.$store.dispatch('accounts/getAccountStatuses')
      }).catch(err => {
        this.handleError(err)
      })
    },
    removeStatus (statusId, statusIndex) {
      if (!statusId && this.statuses[statusIndex].name.length === 0) {
        this.statuses.splice(statusIndex, 1)
        return
      }

      this.$buefy.dialog.confirm({
        title: 'Confirm Deletion',
        message: 'Are you sure you wish to delete this status? This cannot be undone.',
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          if (!statusId) {
            this.statuses.splice(statusIndex, 1)
            return
          }

          this.loadingStatuses = true
          apiCall({
            url: 'statuses/' + statusId,
            method: 'DELETE'
          }).then(response => {
            this.loadingStatuses = false
            this.statuses.splice(statusIndex, 1)
            this.$awn.success(response.message)
          }).catch(err => {
            this.loadingStatuses = false
            this.handleError(err)
          })
        }
      })
    },
    updateCompany () {
      this.loading = true

      if (this.logo.name && this.logo_url === '') {
        return this.uploadLogo()
      }

      this.$store.dispatch(COMPANY_UPDATE, {
        'id': this.companyId,
        'name': this.companyName,
        'language': this.language,
        'timezone': this.timezone,
        'logo_url': this.logo_url
      }).then(response => {
        this.loading = false
        this.$store.dispatch(INIT_GET)
        this.$awn.success(response.message)
      }).catch(err => {
        this.loading = false
        this.handleError(err)
      })
    },
    updateStatus: _.debounce(function (statusId, statusIndex) {
      if (this.statuses[statusIndex].name.length === 0) {
        return
      }

      if (statusId === undefined) {
        return this.createStatus(statusIndex)
      }

      apiCall({ // Get signed URL to upload
        url: 'statuses/' + statusId,
        data: this.statuses[statusIndex],
        method: 'PUT'
      }).then(response => {
        this.$awn.success(response.message)
        this.$store.dispatch('accounts/getAccountStatuses')
      }).catch(err => {
        this.handleError(err)
      })
    }, 900),
    uploadLogo () {
      apiCall({ // Get signed URL to upload
        url: 'companies/' + this.companyId + '/files/signed-url',
        data: {
          'filePath': this.logo.name,
          'contentType': this.logo.type
        },
        method: 'POST'
      }).then(response => {
        const baseURL = response.postEndpoint
        const signature = response.signature
        let formData = new FormData()

        for (let key in signature) {
          if (!signature.hasOwnProperty(key)) continue
          formData.append(key, signature[key])
        }

        formData.append('file', this.logo)

        this.$api({ // Upload file to S3
          timeout: 30000,
          method: 'POST',
          baseURL: baseURL,
          data: formData
        }).then(() => {
          this.logo_url = process.env.CLOUDFRONT + '/' + signature.key
          this.updateCompany()
          return true
        }).catch(() => {
          this.logo_url = ''
        })
      }).catch(() => {
        this.logo_url = ''
      })

      return false
    },
    clearError (field) {
      this.$set(this.fieldSet, field, {
        'type': '',
        'message': ''
      })
    }
  },
  created () {
    this.setPageTitle('Settings')
    if (this.init) {
      if (this.userCan('view_company_details', 'companies')) {
        this.getCompany()
      }
      this.getCompanyStatuses()
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Settings.vue